import { useState } from 'react'
import OnlinePaymentService from 'services/OnlinePayment'
import PixCharge from './PixCharge'
import CardForm from './CardForm'
import { MdPix } from 'react-icons/md'
import * as FA from 'react-icons/fa'

import {
  SubContainer,
  Form,
  SummaryCard,
  SummaryRow,
  SummaryLabel,
  Divider,
  MethodRow,
  MethodButton,
  Title,
  SubTitle,
  InfoText,
  MoneyTextMask
} from '../styles'

/*
  Passo do sinal, e o passo que cria o agendamento.

  Quando o salão exige pagamento adiantado, o horário ainda não é do cliente quando esta tela
  abre: ele está **reservado** enquanto a cobrança existe, e vira agendamento quando o
  pagamento é confirmado. Por isso não há como pular daqui.

  O valor mostrado começa como estimativa e passa a ser o número da cobrança assim que ela
  nasce — quem calcula o que será cobrado é a API, a partir do preço do vínculo e do
  percentual do salão. O front nunca envia valor.
*/
export default function Payment ({
  business = {},
  customer = null,
  intended = [],
  config = {},
  onPaid: handlePaid = () => null,
  onExpired: handleExpired = () => null
}) {
  /*
    Os meios vêm do servidor, e não de uma lista aqui, porque a liberação do cartão depende do
    gerente do Asaas — por conta, e em produção com análise. Enquanto ela não sair,
    `DepositConfigService.availableMethods` devolve só Pix e o botão de cartão simplesmente não
    aparece, em vez de o cliente digitar o cartão inteiro para receber erro no fim.
  */
  const methods = config.methods ?? []
  const [method, setMethod] = useState (methods[0] ?? null)

  /*
    A cobrança, depois que ela nasce. O valor dela é o que a API cobrou de verdade, e substitui
    a estimativa: o preço de um serviço é por salão e vive no vínculo, então o que o link tem
    em mãos antes da cobrança pode estar zerado.
  */
  const [charge, setCharge] = useState (null)

  const estimatedTotal = OnlinePaymentService.getTotalValue (intended)
  const estimatedDeposit = OnlinePaymentService.getDepositValue (
    intended, config.depositPercentage
  )

  const deposit = charge?.value ?? estimatedDeposit
  const total = charge?.total ?? estimatedTotal
  const remaining = Math.max (total - deposit, 0)

  return (
    <SubContainer>
      <Form>
        <Title>Pagar o sinal</Title>
        <InfoText style={{marginBottom: 12}}>
          Seu horário está reservado enquanto você paga. O agendamento é confirmado assim que
          o pagamento cair, e o restante você paga no salão.
        </InfoText>
        <SummaryCard>
          <SummaryRow>
            <SummaryLabel>Total dos serviços</SummaryLabel>
            <MoneyTextMask value={total}/>
          </SummaryRow>
          <Divider/>
          <SummaryRow>
            <SummaryLabel>
              {`Sinal agora (${config.depositPercentage}%)`}
            </SummaryLabel>
            <SubTitle style={{fontSize: 16, color: '#44CF6C'}}>
              <MoneyTextMask value={deposit} style={{fontSize: 16}}/>
            </SubTitle>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Restante no salão</SummaryLabel>
            <MoneyTextMask value={remaining}/>
          </SummaryRow>
        </SummaryCard>
        {methods.length > 1 && (
          <MethodRow>
            {methods.includes ('pix') && (
              <MethodButton
                selected={method === 'pix'}
                aria-pressed={method === 'pix'}
                onClick={() => setMethod ('pix')}
              >
                <MdPix size={18} color='#32BCAD'/>
                Pix
              </MethodButton>
            )}
            {methods.includes ('credit_card') && (
              <MethodButton
                selected={method === 'credit_card'}
                aria-pressed={method === 'credit_card'}
                onClick={() => setMethod ('credit_card')}
              >
                <FA.FaCreditCard size={16} color='#252525'/>
                Cartão
              </MethodButton>
            )}
          </MethodRow>
        )}
        {method === 'pix' && (
          <PixCharge
            businessId={business.id}
            customerId={customer?.id}
            intended={intended}
            value={deposit}
            onPaid={handlePaid}
            onCharged={setCharge}
            onExpired={handleExpired}
          />
        )}
        {method === 'credit_card' && (
          <CardForm
            businessId={business.id}
            customerId={customer?.id}
            intended={intended}
            value={deposit}
            customer={customer}
            onPaid={handlePaid}
            onCharged={setCharge}
          />
        )}
      </Form>
    </SubContainer>
  )
}
